import type {CSSProperties, ReactNode} from "react";

/** Admin top header height — keep in sync with header.tsx `h-[48px]`. */
export const ADMIN_PAGE_HEADER_HEIGHT_PX = 48;

/** Mobile bottom nav / sidebar dock height used by full-bleed module panels. */
export const ADMIN_SIDEBAR_BOTTOM_HEIGHT_PX = 58;

/** Collapsed desktop sidebar width — keep in sync with sidebar.tsx / adminPageScaffold grid. */
export const ADMIN_SIDEBAR_WIDTH_COLLAPSED_PX = 54;
/** Expanded desktop sidebar width — keep in sync with sidebar.tsx / adminPageScaffold grid. */
export const ADMIN_SIDEBAR_WIDTH_EXPANDED_PX = 240;

/**
 * CSS variable for fixed docks/footers. Hosted Ottokart pages run in
 * WorkspaceConfigScaffold, which sets the same variable to Intellsys sidebar
 * widths (54 collapsed / 294 expanded). Do not wrap hosted pages in this scaffold.
 */
export const ADMIN_SIDEBAR_OFFSET_CSS_VAR = "--admin-sidebar-offset";

export function adminSidebarOffsetStyle(isExpanded: boolean): CSSProperties {
    return {
        [ADMIN_SIDEBAR_OFFSET_CSS_VAR]: isExpanded
            ? `${ADMIN_SIDEBAR_WIDTH_EXPANDED_PX}px`
            : `${ADMIN_SIDEBAR_WIDTH_COLLAPSED_PX}px`,
    };
}
import type {AppSwitcherConfig} from "@vesta/aergia/components/pageLayoutSwitchSites";
import {withMenuItemAnalytics} from "@vesta/aergia/components/pageLayoutSwitchSites";
import type {LinkItemConfigProps} from "@vesta/aergia/components/sideNavLinkMenuItem";
import DummyIcon from "@vesta/aergia/svgs/core/glyph.svg?react";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";

export enum SidebarMenuEnum {
    quickAccess = "5c530650-af10-4842-ab15-32e54f09c118",
    copilot = "5cb2a9b4-597a-44a6-9c1a-587904e00aaf",
    reports = "2e7a57b2-947e-477c-9845-dac252a6ae0c",
    collection = "26f82d21-691b-4ddc-94cd-dc287521afb7",
    favourites = "e2f8b2b7-ead8-49ab-8143-d8b002b313ab",
    dataSources = "8e6f7f0d-f29d-4d27-adbe-c38c513e770a",
}

export type SidebarMenuConfigItem =
    | ({
          type: "linkItem";
          urlPattern?: RegExp;
          mobileChildren?: string;
      } & LinkItemConfigProps & {id: SidebarMenuEnum | Uuid})
    | {
          type: "nestedLinkGroup";
          id: SidebarMenuEnum | Uuid;
          parent: LinkItemConfigProps & {id: SidebarMenuEnum | Uuid; urlPattern?: RegExp};
          children: Array<LinkItemConfigProps & {id: SidebarMenuEnum | Uuid; urlPattern?: RegExp}>;
      }
    | {
          type: "topLevelSpacer";
          id: Uuid;
      }
    | {
          type: "sectionHeading";
          id: Uuid;
          children: string;
      };

export type SidebarMenuBottomItem = {
    expanded: {
        href: string;
        logo: ReactNode;
    };
    collapsed: {
        href: string;
        logo: ReactNode;
    };
};

export const appSwitcherMenuSections = withMenuItemAnalytics([
    {
        id: "bacb2262-55af-4b90-83b9-765069e890b0",
        hasSeparator: true,
        items: [
            {
                type: "link",
                id: "91cc2dd5-b43d-4627-b93c-61bd12fc79d1",
                href: "/",
                iconBefore: (props) => <DummyIcon {...props} />,
                title: "Main Page name for all the apps",
                paddingInline: "px-ds-space-100",
                className: "rounded-[3px]",
            },
            {
                type: "link",
                id: "2b3a4761-b21c-4af3-bf9e-02ff25b72390",
                href: "/",
                iconBefore: (props) => <DummyIcon {...props} />,
                title: "App Name 1",
                paddingInline: "px-ds-space-100",
                className: "rounded-[3px]",
            },
            {
                type: "link",
                id: "1dadf378-e4b3-49fa-804b-657dd6231292",
                iconBefore: (props) => <DummyIcon {...props} />,
                title: "App Name 2",
                href: "",
                paddingInline: "px-ds-space-100",
                className: "rounded-[3px]",
            },
            {
                type: "link",
                id: "0f2fa1eb-6e3b-4153-966b-d464eed6a99d",
                iconBefore: (props) => <DummyIcon {...props} />,
                title: "App Name 3",
                href: "",
                paddingInline: "px-ds-space-100",
                className: "rounded-[3px]",
            },
        ],
    },
    {
        id: "d5f9acb7-6b2e-45f6-8de2-c9e5ceafdfdc",
        hasSeparator: true,
        title: "Recommeded Products",
        items: [
            {
                type: "link",
                id: "9fcd8799-d6ac-4eec-9763-82ccc2cf36da",
                href: "/",
                iconBefore: (props) => <DummyIcon {...props} />,
                title: "App Name 1",
                paddingInline: "px-ds-space-100",
                className: "rounded-[3px]",
            },
            {
                type: "link",
                id: "f7a6b1ac-43eb-4a86-b54f-11969fed067a",
                iconBefore: (props) => <DummyIcon {...props} />,
                title: "App Name 2",
                href: "",
                paddingInline: "px-ds-space-100",
                className: "rounded-[3px]",
            },
            {
                type: "link",
                id: "84e8be5d-deb0-492f-8475-d21d70a6fe74",
                iconBefore: (props) => <DummyIcon {...props} />,
                title: "App Name 3",
                href: "",
                paddingInline: "px-ds-space-100",
                className: "rounded-[3px]",
                iconAfter: (props) => <DummyIcon {...props} />,
            },
        ],
    },
    {
        id: "2c299e30-cc7e-4fde-9257-eef44f3ac840",
        hasSeparator: true,
        title: "Explore All Products",
        items: [
            {
                type: "link",
                id: "2a2fd698-c205-47aa-9c4a-1803994cd27c",
                href: "/",
                iconBefore: (props) => <DummyIcon {...props} />,
                title: "App Name 1",
                description: "This is a rough text",
                paddingInline: "px-ds-space-100",
                className: "rounded-[3px]",
            },
            {
                type: "link",
                id: "2618d64e-ff5f-4f06-93cb-ae564a579932",
                title: "App Name 2",
                href: "/",
                paddingInline: "px-ds-space-100",
                className: "rounded-[3px]",
            },
            {
                type: "link",
                id: "4e20db73-db77-4307-af19-ca5698ba0ce2",
                iconBefore: (props) => <DummyIcon {...props} />,
                title: "App Name 3",
                href: "",
                paddingInline: "px-ds-space-100",
                className: "rounded-[3px]",
                iconAfter: (props) => <DummyIcon {...props} />,
            },
        ],
    },
]);

export const appSwitcherConfig: AppSwitcherConfig[] = [
    {
        id: "f36bcaf9-bfca-47f9-9fa4-9b0d51566744",
        icon: (props) => <DummyIcon {...props} />,
        heading: "Team Alpha",
        description: "alpha.team@example.com",
        sections: appSwitcherMenuSections,
    },
    {
        id: "9cdb67ac-0f31-4239-96bc-7b6a71c1f979",
        icon: (props) => <DummyIcon {...props} />,
        heading: "Team Beta",
        description: "beta.team@example.com",
        sections: appSwitcherMenuSections.slice(2),
    },
];
